import { Stack, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Card } from "~/components/Card";
import {
  DetailsLayout,
  HistoryCard,
  LockCard,
  ManageCard,
  RelatedResourceLink,
  ResourceFields,
} from "~/components/DetailsCards";
import {
  Form,
  FormSkeleton,
  getChangedFields,
  ObjectField,
  TextField,
  useStudioForm,
} from "~/components/Form";
import { calculateProcessRefetchInterval, Process } from "~/components/Process";
import { QueryRenderer } from "~/components/QueryRenderer";
import {
  useDeleteDigestion,
  useDigestion,
  useUpdateDigestion,
} from "~/domain/datastores";
import { pick } from "~/lib/std";
import {
  makeDigestionPartsLocation,
  makeDigestionTopicsLocation,
  makeDigestionsLocation,
  makeEditDigestionLocation,
  useDigestionParams,
} from "~/paths";
import type { Digestion } from "~/services/datastore";
import { selectData } from "~/utils";
import { editDigestionSchema } from "../schemas";

export function DigestionDetails() {
  const { digestionId } = useDigestionParams();

  const query = useDigestion(digestionId, {
    select: selectData,
    refetchInterval: calculateProcessRefetchInterval,
  });

  const generalSection = <GeneralSection query={query} />;
  const processSection = (
    <Process query={query} mutation={useUpdateDigestion(digestionId)} />
  );
  const relatedResourcesSection = (
    <Stack spacing={2} useFlexGap>
      <Typography variant="h4" component="h2">
        Related Resources
      </Typography>
      <RelatedResourceLink
        text="Topics"
        to={makeDigestionTopicsLocation({ digestionId })}
      />
      <RelatedResourceLink
        text="Parts"
        to={makeDigestionPartsLocation({ digestionId })}
      />
    </Stack>
  );
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="digestion"
      query={query}
      updateMutation={useUpdateDigestion(digestionId)}
    />
  );
  const historySection = <HistoryCard query={query} />;
  const manageSection = (
    <ManageCard
      resourceName="digestion"
      query={query}
      editLocation={makeEditDigestionLocation({ digestionId })}
      deleteMutation={useDeleteDigestion(digestionId)}
      getReadableName={(digestion) => digestion.name ?? digestion.id}
      listLocation={makeDigestionsLocation()}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {processSection}
          {relatedResourcesSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {processSection}
          {lockSection}
          {historySection}
          {manageSection}
          {relatedResourcesSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Digestion> }) {
  return (
    <Card title="General">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          { dataType: "log", accessor: "logId" },
        ]}
      />
    </Card>
  );
}

function InfoSection({ query }: { query: UseQueryResult<Digestion> }) {
  return (
    <Card>
      <QueryRenderer
        query={query}
        loading={<FormSkeleton shapes={["text", "multiline", "multiline"]} />}
        success={(digestion) => <InfoSectionImpl digestion={digestion} />}
      />
    </Card>
  );
}

function InfoSectionImpl({ digestion }: { digestion: Digestion }) {
  const updateDigestion = useUpdateDigestion(digestion.id);

  const { enqueueSnackbar } = useSnackbar();

  const schema = editDigestionSchema.pick({
    name: true,
    note: true,
    context: true,
  });
  const FIELDS = schema.keyof().options;

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useStudioForm({
    schema,
    defaultValues: pick(digestion, FIELDS),
    onSubmit(values) {
      const changedFields = getChangedFields(values, dirtyFields);

      updateDigestion.mutate(changedFields, {
        onSuccess(response) {
          enqueueSnackbar("Digestion updated", { variant: "success" });

          reset(pick(response.data, FIELDS));
        },
        onError() {
          enqueueSnackbar("Unable to update digestion", { variant: "error" });
        },
      });
    },
  });

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        loading={updateDigestion.isLoading}
        submitText="Save Changes"
        disabled={digestion.locked}
      >
        <TextField control={control} name="name" />
        <TextField control={control} name="note" multiline />
        <ObjectField control={control} name="context" />
      </Form>
      {digestion.locked && (
        <Typography variant="body2">
          Unlock this digestion to make updates.
        </Typography>
      )}
    </>
  );
}
